import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '../ui/Tooltip';
import { useColorTheme } from '../../services/colorThemeService';
import { formatValue, formatNumber } from '../../utils/displayFormat';
import type { DisplayFormat } from '../../utils/displayFormat';


interface AccountStats {
  totalTrades: number;
  winningTradesCount: number;
  losingTradesCount: number;
  breakEvenTradesCount: number;
  winRate: number;
  totalProfit: number;
  totalReturnPercentage: number;
  currentBalance: number;
  maxBalance: number;
  profitFactor: { profits: number; losses: number };
  averageRiskReward: number;
  maxDrawdown: number;
  averageTradeReturn: number;
  lastUpdated: Date;
}

interface AccountStatsCardsProps {
  stats: AccountStats;
  currency: string;
  selectedAccount?: any;
  trades?: any[];
  customBalanceCard?: React.ReactNode;
  displayFormat?: DisplayFormat;
  initialBalance?: number;
  /** Affiche uniquement une carte (0=Solde, 1=Win%, 2=PF, 3=R/R) */
  visibleCardIndex?: 0 | 1 | 2 | 3;
}

export const AccountStatsCards: React.FC<AccountStatsCardsProps> = ({ 
  stats, 
  currency, 
  customBalanceCard,
  displayFormat = 'currency',
  initialBalance = 10000,
  visibleCardIndex,
}) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Hook pour les thèmes de couleurs
  const { getThemeColors } = useColorTheme();
  const previewMode = visibleCardIndex !== undefined;
  
  // Fonction pour obtenir le symbole de la devise
  const getCurrencySymbol = (currencyCode: string): string => {
    const symbols: { [key: string]: string } = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CHF': '₣',
      'CAD': 'C$',
      'AUD': 'A$',
      'NZD': 'NZ$'
    };
    return symbols[currencyCode] || '$'; // Par défaut "$" si la devise n'est pas trouvée
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Déterminer le nombre de colonnes basé sur la largeur de l'écran
  const getGridCols = () => {
    if (windowWidth >= 1415) {
      return 'grid-cols-4'; // 4 colonnes au-dessus de 1415px
    } else if (windowWidth >= 640) {
      return 'grid-cols-2'; // 2 colonnes entre 640px et 1415px
    } else {
      return 'grid-cols-1'; // 1 colonne en dessous de 640px
    }
  };

  // Définition des composants de statistiques
  const statsComponents = [
    // Composant 1 : Solde actuel ou composant personnalisé
    customBalanceCard ? (
      <div key="0" className="flex-1 min-w-[250px] bento-card h-full overflow-visible">
        {customBalanceCard}
      </div>
    ) : (
      <div key="0" className={`flex-1 bento-card p-6 h-full overflow-visible ${previewMode ? 'w-full min-w-0' : 'min-w-[250px]'}`}>
        <div className="flex items-center gap-3 mb-4">
          <div>
            <p className="text-sm text-content-secondary">{t('statsCards.balance')}</p>
            <p className="text-xl font-semibold">
              {formatValue(stats.currentBalance ?? 0, displayFormat, { 
                currency: getCurrencySymbol(currency),
                initialBalance,
                isBalance: true
              })}
            </p>
          </div>
        </div>
        <div className="text-sm">
          <div className="flex items-center" style={{ 
            color: stats.totalProfit >= 0 ? getThemeColors().positive : getThemeColors().negative 
          }}>
            {stats.totalProfit >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
            {formatValue(stats.totalProfit ?? 0, displayFormat, { 
              currency: getCurrencySymbol(currency),
              initialBalance,
              showSign: true
            })}
          </div>
          <p className="text-content-secondary mt-1">
            {t('statsCards.since_start')}
          </p>
        </div>
      </div>
    ),

    // Composant 2 : Win Rate
    <div key="1" className={`flex-1 bento-card p-4 h-full overflow-visible ${previewMode ? 'w-full min-w-0' : 'min-w-[250px]'}`}>
      <div className="flex justify-between items-start overflow-visible">
        <div className="flex flex-col gap-1 overflow-visible">
          <div className="flex items-center gap-1 overflow-visible">
            <span className="text-sm text-content-secondary">{t('statsCards.win_pct')}</span>
            <Tooltip content={
              <div className="text-left">
                Pourcentage de trades gagnants<br />
                par rapport au nombre total de trades
              </div>
            } position="top">
              <HelpCircle className="h-4 w-4 text-content-secondary hover:text-content-primary cursor-help" />
            </Tooltip>
            <Tooltip content={t('landing.stats.break_even_title')} position="top">
              <button
                onClick={() => navigate('/settings?tab=parametres#break-even')}
                className="h-4 w-4 text-content-secondary hover:text-content-primary cursor-pointer transition-colors flex items-center justify-center"
                aria-label={t('landing.stats.break_even_aria')}
                /* Crochet stable pour la CSS : l'aria-label est traduit, un
                   sélecteur d'attribut sur son texte casserait hors français. */
                data-break-even-settings=""
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
                  <g id="SVGRepo_iconCarrier"> 
                    <path d="M2 18.1136V5.88641C2 4.18426 2 3.33319 2.54242 3.05405C3.08484 2.77491 3.77738 3.26959 5.16247 4.25894L6.74371 5.3884C7.35957 5.8283 7.6675 6.04825 7.83375 6.3713C8 6.69435 8 7.07277 8 7.8296V16.1705C8 16.9273 8 17.3057 7.83375 17.6288C7.6675 17.9518 7.35957 18.1718 6.74372 18.6117L5.16248 19.7411C3.77738 20.7305 3.08484 21.2251 2.54242 20.946C2 20.6669 2 19.8158 2 18.1136Z" stroke="currentColor" strokeWidth="2"></path> 
                    <path d="M22 18.1136V5.88641C22 4.18426 22 3.33319 21.4576 3.05405C20.9152 2.77491 20.2226 3.26959 18.8375 4.25894L17.2563 5.3884C16.6404 5.8283 16.3325 6.04825 16.1662 6.3713C16 6.69435 16 7.07277 16 7.8296V16.1705C16 16.9273 16 17.3057 16.1662 17.6288C16.3325 17.9518 16.6404 18.1718 17.2563 18.6117L18.8375 19.7411C20.2226 20.7305 20.9152 21.2251 21.4576 20.946C22 20.6669 22 19.8158 22 18.1136Z" stroke="currentColor" strokeWidth="2"></path> 
                    <path d="M12 14V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"></path> 
                    <path d="M12 6V2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"></path> 
                    <path d="M12 22V18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"></path> 
                  </g>
                </svg>
              </button>
            </Tooltip>
          </div>
          <div className="text-2xl font-bold">
            {stats.winRate.toFixed(0)}%
          </div>
        </div>

        {/* SECTION : Graphique en arcs */}
        <div className="relative w-[155px] h-[120px] flex items-center justify-center">
          <svg className="w-full h-[85px] -mt-8" viewBox="0 0 160 100">
            {/* Arc de base gris (en arrière-plan) */}
            <path
              d="M10,90 A70,70 0 0 1 150,90"
              className="stroke-background-secondary/30"
              strokeWidth="16"
              strokeLinecap="round"
              fill="none"
            />

            {/* Arcs colorés avec gaps visibles */}
            {(() => {
              const total = stats.winningTradesCount + stats.breakEvenTradesCount + stats.losingTradesCount;
              if (total === 0) return null; // éviter la division par zéro

              // Calcul des ratios
              const winRatio = stats.winningTradesCount / total;
              const beRatio = stats.breakEvenTradesCount / total;
              const lossRatio = stats.losingTradesCount / total;
              const totalAngle = Math.PI; // 180° en radians

              // On augmente l'angle de gap pour une séparation physique plus marquée
              const gap = Math.PI * 0.09; // environ 9° de gap
              
              // Détermine le nombre de segments actifs
              let segmentsCount = 0;
              if (stats.winningTradesCount > 0) segmentsCount++;
              if (stats.breakEvenTradesCount > 0) segmentsCount++;
              if (stats.losingTradesCount > 0) segmentsCount++;

              // Le nombre de gaps est segmentsCount - 1
              const gapCount = segmentsCount > 0 ? segmentsCount - 1 : 0;
              // L'angle total disponible pour les arcs (hors gaps)
              const availableAngle = totalAngle - gap * gapCount;

              // Fonction pour convertir un angle en coordonnées sur l'arc
              const coord = (angle: number) => ({
                x: 10 + 70 * (1 - Math.cos(angle)),
                y: 90 - 70 * Math.sin(angle)
              });

              let currentAngle = 0;
              const paths = [];

              // Segment des trades gagnants (vert)
              if (stats.winningTradesCount > 0) {
                const arcAngle = availableAngle * winRatio;
                const startAngle = currentAngle;
                const endAngle = startAngle + arcAngle;
                const { x: x1, y: y1 } = coord(startAngle);
                const { x: x2, y: y2 } = coord(endAngle);
                paths.push(
                  <path
                    key="win"
                    d={`M ${x1},${y1} A70,70 0 0 1 ${x2},${y2}`}
                    style={{ stroke: getThemeColors().positive }}
                    strokeWidth="16"
                    strokeLinecap="round"
                    fill="none"
                  />
                );
                currentAngle = endAngle + gap;
              }

              // Segment breakeven (bleu)
              if (stats.breakEvenTradesCount > 0) {
                const arcAngle = availableAngle * beRatio;
                const startAngle = currentAngle;
                const endAngle = startAngle + arcAngle;
                const { x: x1, y: y1 } = coord(startAngle);
                const { x: x2, y: y2 } = coord(endAngle);
                paths.push(
                  <path
                    key="be"
                    d={`M ${x1},${y1} A70,70 0 0 1 ${x2},${y2}`}
                    style={{ stroke: getThemeColors().secondary }}
                    strokeWidth="16"
                    strokeLinecap="round"
                    fill="none"
                  />
                );
                currentAngle = endAngle + gap;
              }

              // Segment des trades perdants (rouge)
              if (stats.losingTradesCount > 0) {
                const arcAngle = availableAngle * lossRatio;
                const startAngle = currentAngle;
                const endAngle = startAngle + arcAngle;
                const { x: x1, y: y1 } = coord(startAngle);
                const { x: x2, y: y2 } = coord(endAngle);
                paths.push(
                  <path
                    key="loss"
                    d={`M ${x1},${y1} A70,70 0 0 1 ${x2},${y2}`}
                    style={{ stroke: getThemeColors().negative }}
                    strokeWidth="16"
                    strokeLinecap="round"
                    fill="none"
                  />
                );
              }

              return paths;
            })()}
          </svg>

          {/* SECTION : Légende des valeurs */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-between px-1">
            {(() => {
              // Helper function to create color with opacity
              const createColorWithOpacity = (color: string, opacity: number): string => {
                if (color.startsWith('rgba')) {
                  // Replace the existing opacity
                  return color.replace(/[\d.]+\)$/, `${opacity})`);
                } else if (color.startsWith('rgb')) {
                  // Convert rgb to rgba
                  return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
                } else if (color.startsWith('#')) {
                  // Convert hex to rgba
                  const hex = color.replace('#', '');
                  const r = parseInt(hex.substr(0, 2), 16);
                  const g = parseInt(hex.substr(2, 2), 16);
                  const b = parseInt(hex.substr(4, 2), 16);
                  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
                }
                return color;
              };
              
              const colors = getThemeColors();
              
              return (
                <>
                  <div className="flex items-center justify-center w-7 h-7 rounded-full text-xs" style={{ 
                    backgroundColor: createColorWithOpacity(colors.positive, 0.2), 
                    color: colors.positive 
                  }}>
                    {formatNumber(stats.winningTradesCount, displayFormat)}
                  </div>
                  <div className="flex items-center justify-center w-7 h-7 rounded-full text-xs" style={{ 
                    backgroundColor: createColorWithOpacity(colors.secondary, 0.2), 
                    color: colors.secondary 
                  }}>
                    {formatNumber(stats.breakEvenTradesCount, displayFormat)}
                  </div>
                  <div className="flex items-center justify-center w-7 h-7 rounded-full text-xs" style={{ 
                    backgroundColor: createColorWithOpacity(colors.negative, 0.2), 
                    color: colors.negative 
                  }}>
                    {formatNumber(stats.losingTradesCount, displayFormat)}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>,

    // Composant 3 : Facteur de profit
    <div key="2" className={`flex-1 bento-card p-4 h-full overflow-visible ${previewMode ? 'w-full min-w-0' : 'min-w-[250px]'}`}>
      <div className={previewMode ? 'flex items-start justify-between gap-6' : 'flex flex-col gap-1 overflow-visible'}>
        <div className="flex flex-col gap-1 overflow-visible shrink-0">
        <div className="flex items-center gap-1 overflow-visible">
          <span className="text-sm text-content-secondary">{t('statsCards.profit_factor')}</span>
          <Tooltip content={
            <div className="text-left">
              Ratio entre les profits totaux<br />
              et les pertes totales.<br />
              Un facteur supérieur à 1 indique<br />
              une stratégie profitable
            </div>
          } position="top">
            <HelpCircle className="h-4 w-4 text-content-secondary hover:text-content-primary cursor-help" />
          </Tooltip>
        </div>
        <span className="text-2xl font-bold text-content-primary" style={{ minWidth: 60, textAlign: 'left' }}>{stats.profitFactor.losses === 0 ? (stats.profitFactor.profits > 0 ? '∞' : '0.00') : (stats.profitFactor.profits / Math.abs(stats.profitFactor.losses)).toFixed(2)}</span>
        </div>
        {/* Barre horizontale win/loss, plus fine et alignée, sous le chiffre */}
        <div className={`flex flex-col justify-center ${previewMode ? 'flex-1 min-w-[180px] max-w-[280px]' : 'flex-1 mt-2 max-w-[190px] ml-auto w-full'}`}>
          <div className="flex items-center w-full h-[10px] rounded-[6px] overflow-hidden bg-background-secondary/30">
            {(() => {
              const profits = stats.profitFactor.profits;
              const losses = Math.abs(stats.profitFactor.losses);
              const total = profits + losses;
              
              if (total === 0) {
                return (
                  <>
                    <div style={{ width: '50%', backgroundColor: getThemeColors().positive }} className="h-full transition-all rounded-[6px]"></div>
                    <div style={{ width: '7px', minWidth: '7px', maxWidth: '7px', background: 'transparent' }}></div>
                    <div style={{ width: '50%', backgroundColor: getThemeColors().negative }} className="h-full transition-all rounded-[6px]"></div>
                  </>
                );
              }
              
              // Calculer les ratios de base
              let profitRatio = profits / total;
              let lossRatio = losses / total;
              
              // Appliquer une taille minimale de 10% pour chaque segment s'il existe
              const minVisibleRatio = 0.1;
              const hasProfit = profits > 0;
              const hasLoss = losses > 0;
              
              if (hasProfit && hasLoss) {
                // Les deux existent, appliquer la taille minimale
                if (profitRatio < minVisibleRatio) {
                  profitRatio = minVisibleRatio;
                  lossRatio = 1 - minVisibleRatio;
                } else if (lossRatio < minVisibleRatio) {
                  lossRatio = minVisibleRatio;
                  profitRatio = 1 - minVisibleRatio;
                }
              }
              
              return (
                <>
                  {hasProfit && (
                    <div style={{ width: `${profitRatio * 100}%`, backgroundColor: getThemeColors().positive }} className="h-full transition-all rounded-[6px]"></div>
                  )}
                  {hasProfit && hasLoss && (
                    <div style={{ width: '7px', minWidth: '7px', maxWidth: '7px', background: 'transparent' }}></div>
                  )}
                  {hasLoss && (
                    <div style={{ width: `${lossRatio * 100}%`, backgroundColor: getThemeColors().negative }} className="h-full transition-all rounded-[6px]"></div>
                  )}
                </>
              );
            })()}
          </div>
          <div className={`flex justify-between gap-3 mt-1 font-semibold ${previewMode ? 'text-sm' : 'text-base'}`}>
            <span className="whitespace-nowrap tabular-nums" style={{ color: getThemeColors().positive }}>
              {formatValue(stats.profitFactor.profits, displayFormat, { 
                currency: getCurrencySymbol(currency),
                initialBalance
              })}
            </span>
            <span className="whitespace-nowrap tabular-nums text-right" style={{ color: getThemeColors().negative }}>
              -{formatValue(Math.abs(stats.profitFactor.losses), displayFormat, { 
                currency: getCurrencySymbol(currency),
                initialBalance
              })}
            </span>
          </div>
        </div>
      </div>
    </div>,

    // Composant 4 : Ratio R/R moyen
    <div key="3" className={`flex-1 bento-card p-4 h-full overflow-visible ${previewMode ? 'w-full min-w-0' : 'min-w-[250px]'}`}>
      <div className="flex items-start gap-4 overflow-visible">
        <div className="flex flex-col gap-1 overflow-visible">
          <div className="flex items-center gap-1 overflow-visible">
            <span className="text-sm text-content-secondary">{t('statsCards.rr_ratio')}</span>
            <Tooltip content={
              <div className="text-left">
                Ratio moyen entre le profit potentiel<br />
                (Reward) et la perte potentielle<br />
                (Risk) de vos trades
              </div>
            } position="top">
              <HelpCircle className="h-4 w-4 text-content-secondary hover:text-content-primary cursor-help" />
            </Tooltip>
          </div>
          <div className="text-2xl font-bold">
            {stats.averageRiskReward.toFixed(2)}
          </div>
          {/* Légende déplacée ici */}
          <div className="flex flex-col gap-2 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getThemeColors().positive }}></div>
              <span className="text-xs text-content-secondary">{t('statsCards.reward')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getThemeColors().negative }}></div>
              <span className="text-xs text-content-secondary">{t('statsCards.risk')}</span>
            </div>
          </div>
        </div>

        {/* SECTION : Graphique en donut pour R/R */}
        <div className="flex-1 flex items-center justify-center min-h-[120px]">
          <div className="relative w-[120px] h-[120px]">
            <svg className="w-full h-full transform -rotate-90">
              {/* Cercle de base (gris) */}
              <circle
                cx="60"
                cy="60"
                r="50"
                fill="none"
                className="stroke-background-secondary/30"
                strokeWidth="12"
                strokeLinecap="round"
              />
              {(() => {
                const circumference = 2 * Math.PI * 50;
                const total = stats.averageRiskReward + 1; // Risk (1) + Reward
                const rewardRatio = stats.averageRiskReward / total;
                const riskRatio = 1 / total;
                const gap = 0.12; // 12% de gap entre les segments
                
                // Ajuster les ratios pour tenir compte du gap
                const adjustedRewardRatio = rewardRatio * (1 - gap);
                const adjustedRiskRatio = riskRatio * (1 - gap);
                
                // Calculer les longueurs d'arc et les offsets
                const rewardArcLength = circumference * adjustedRewardRatio;
                const riskArcLength = circumference * adjustedRiskRatio;
                const gapOffset = circumference * gap / 2;
                
                return (
                  <>
                    {/* Segment Reward (vert) */}
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      style={{ stroke: getThemeColors().positive }}
                      strokeWidth="12"
                      strokeDasharray={`${rewardArcLength} ${circumference}`}
                      strokeLinecap="round"
                    />
                    {/* Segment Risk (rouge) */}
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      style={{ stroke: getThemeColors().negative }}
                      strokeWidth="12"
                      strokeDasharray={`${riskArcLength} ${circumference}`}
                      strokeDashoffset={`${-(rewardArcLength + gapOffset)}`}
                      strokeLinecap="round"
                    />
                  </>
                );
              })()}
            </svg>
            {/* Texte au centre */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xs text-content-secondary">R/R</span>
              <span className="text-sm font-semibold">{stats.averageRiskReward.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  ];

  if (visibleCardIndex !== undefined) {
    return (
      <div className="h-full w-full">
        <div className="relative" style={{ overflow: 'visible' }}>
          {statsComponents[visibleCardIndex]}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <div className={`grid ${getGridCols()} h-full w-full gap-4`}>
        {statsComponents.map((component, index) => (
          <div key={index} className="relative" style={{ overflow: 'visible' }}>
            {component}
          </div>
        ))}
      </div>
    </div>
  );
};
